import { createApi } from "@reduxjs/toolkit/query/react";
import { appBaseQuery } from "config/api.config";
import {
  AddTHParams,
  AddTHsParams,
  AutoCompleteValues,
  ErhebungsdatenPersonData,
  FindTreuhaenderParams,
  FindTreuhandstelleParams,
  LandwirtKundensatz,
  LandwirtMasterdata,
  Masterdata,
  MasterdataSearchParams,
  RekStatusList,
  RekSubStatusList,
  SaveLieferterminParams,
  SetTreuhaenderContactMailParams,
  Treuhaender,
  Treuhandstelle,
  UpdateLandwirtParams,
  UpdateTHsParams,
  UpdateTreuhaenderParams,
  ValidateAndGetLandwirtData,
  ValidateAndGetLandwirtDataParams,
  ValidationErrors,
} from "types/masterdata.types";

export const masterdataApi = createApi({
  reducerPath: "masterdata",
  baseQuery: appBaseQuery,
  endpoints: (builder) => ({
    getMasterdata: builder.query<
      Masterdata,
      MasterdataSearchParams | undefined
    >({
      query: (params) => ({
        url: "Masterdata/Find",
        params,
      }),
    }),
    getZammadBaseLink: builder.query<string, void>({
      query: () => ({
        url: "Masterdata/GetZammadBaseLink",
        async responseHandler(response) {
          return response.text();
        },
      }),
    }),
    getTreuhandstelleAutoComplete: builder.query<AutoCompleteValues, void>({
      query: () => ({
        url: "Treuhand/GetTreuhandstellenShort",
      }),
    }),
    getLandwirtKundensatz: builder.query<
      LandwirtKundensatz,
      { kundennummer: number } | undefined
    >({
      query: (params) => ({
        url: "Masterdata/GetLandwirtKundensatz",
        params,
      }),
    }),
    getTreuhandstelle: builder.query<
      Treuhandstelle,
      { kundennummer: number } | undefined
    >({
      query: (params) => ({
        url: "Treuhand/GetTreuhandstelle",
        params,
      }),
    }),
    getTreuhandstelleById: builder.query<
      Treuhandstelle,
      { treuhandstelleId: number } | undefined
    >({
      query: (params) => ({
        url: "Treuhand/GetTreuhandstelleById",
        params,
      }),
    }),
    getTreuhaender: builder.query<
      Treuhaender,
      { kundennummer: number } | undefined
    >({
      query: (params) => ({
        url: "Treuhand/GetTreuhaender",
        params,
      }),
    }),
    getTreuhaenderById: builder.query<
      Treuhaender,
      { treuhaenderId: number } | undefined
    >({
      query: (params) => ({
        url: "Treuhand/GetTreuhaenderById",
        params,
      }),
    }),
    getErhebungsdatenPersonData: builder.query<
      ErhebungsdatenPersonData,
      { kundennummer: number } | undefined
    >({
      query: (params) => ({
        url: "Erhebungsdaten/GetErhebungsdatenPersonData",
        params,
      }),
    }),
    validateAndGetLandwirtData: builder.mutation<
      ValidateAndGetLandwirtData,
      ValidateAndGetLandwirtDataParams
    >({
      query: (params) => ({
        method: "GET",
        url: "Landwirt/ValidateAndGetLandwirtData",
        params,
      }),
    }),
    updateLandwirt: builder.mutation<ValidationErrors, UpdateLandwirtParams>({
      query: (params) => ({
        method: "POST",
        url: "Landwirt/UpdateLandwirt",
        body: params,
      }),
    }),
    getLandwirtMasterdataForTreuhaender: builder.query<
      LandwirtMasterdata[],
      { treuhaenderId: number } | undefined
    >({
      query: (params) => ({
        url: "Masterdata/GetLandwirtMasterdataForTreuhaender",
        params,
      }),
    }),
    updateTreuhaender: builder.mutation<
      ValidationErrors,
      UpdateTreuhaenderParams
    >({
      query: (params) => ({
        method: "POST",
        url: "Treuhand/UpdateTreuhaender",
        body: params,
      }),
    }),
    getTreuhaenderMasterdataForTreuhandstelle: builder.query<
      LandwirtMasterdata[],
      { treuhandstelleId: number } | undefined
    >({
      query: (params) => ({
        url: "Masterdata/GetTreuhaenderMasterdataForTreuhandstelle",
        params,
      }),
    }),
    updateTreuhandstelle: builder.mutation<ValidationErrors, UpdateTHsParams>({
      query: (params) => ({
        method: "POST",
        url: "Treuhand/UpdateTreuhandstelle",
        body: params,
      }),
    }),
    addTreuhaender: builder.mutation<ValidationErrors, AddTHParams>({
      query: (params) => {
        const { treuhandstelleId, ...thData } = params;
        return {
          method: "POST",
          url: "Treuhand/AddTreuhaender",
          body: thData,
          params: { treuhandstelleId },
        };
      },
    }),
    findTreuhandstelle: builder.query<
      Treuhandstelle[],
      FindTreuhandstelleParams | undefined
    >({
      query: (params) => ({
        url: "Treuhand/FindTreuhandstelle",
        params,
      }),
    }),
    findTreuhaender: builder.query<
      Treuhaender[],
      FindTreuhaenderParams | undefined
    >({
      query: (params) => ({
        url: "Treuhand/FindTreuhaender",
        params,
      }),
    }),
    setDatenlieferantLandwirt: builder.mutation<
      ValidationErrors,
      { kundennummer: number }
    >({
      query: (params) => ({
        method: "POST",
        url: "Masterdata/SetDatenlieferantLandwirt",
        params,
      }),
    }),
    addExistingTreuhaenderToKundensatz: builder.mutation<
      ValidationErrors,
      { kundennummer: number; treuhaenderId: number }
    >({
      query: (params) => ({
        method: "POST",
        url: "Masterdata/AddExistingTreuhaenderToKundensatz",
        params,
      }),
    }),
    addExistingTreuhandstelleToKundensatz: builder.mutation<
      ValidationErrors,
      { kundennummer: number; treuhandstelleId: number }
    >({
      query: (params) => ({
        method: "POST",
        url: "Masterdata/AddExistingTreuhandstelleToKundensatz",
        params,
      }),
    }),
    addTreuhandstelle: builder.mutation<ValidationErrors, AddTHsParams>({
      query: (params) => {
        const { kundennummer, ...thsData } = params;
        return {
          method: "POST",
          url: "Treuhand/AddTreuhandstelle",
          body: thsData,
          params: { kundennummer },
        };
      },
    }),
    activateThsQuestionnaire: builder.mutation<
      ValidationErrors,
      {
        treuhandstelleId: number;
      }
    >({
      query: (params) => ({
        method: "POST",
        url: "treuhand/ActivatePassiveThsParticipant",
        params,
      }),
    }),
    getLieferterminList: builder.query<string[], void>({
      query: () => ({
        url: "Masterdata/GetLieferterminList",
      }),
    }),
    saveLiefertermin: builder.mutation<
      ValidationErrors,
      SaveLieferterminParams
    >({
      query: (params) => ({
        method: "POST",
        url: "Masterdata/SaveLiefertermin",
        params,
      }),
    }),
    setTreuhaenderContactMail: builder.mutation<
      ValidationErrors,
      SetTreuhaenderContactMailParams
    >({
      query: (params) => ({
        method: "POST",
        url: "Masterdata/SetTreuhaenderContactMail",
        params,
      }),
    }),
    deleteTreuhaenderContactMail: builder.mutation<
      ValidationErrors,
      {
        kundennummer: number;
      }
    >({
      query: (params) => ({
        method: "POST",
        url: "Masterdata/DeleteTreuhaenderContactMail",
        params,
      }),
    }),
    getPossibleRekSubStatusList: builder.mutation<
      RekSubStatusList,
      { rekStatus: number; isNachkodierung: boolean }
    >({
      query: (params) => ({
        method: "POST",
        url: "Masterdata/GetPossibleRekSubStatusList",
        params,
      }),
    }),
    getPossibleRekStatusList: builder.mutation<
      RekStatusList,
      { rekStatus: number; kundennummer: number }
    >({
      query: (params) => ({
        method: "POST",
        url: "Masterdata/GetPossibleRekStatusList",
        params,
      }),
    }),
    setRekStatus: builder.mutation<
      ValidationErrors,
      {
        kundennummer: number;
        rekSubStatus: number;
        rekStatus: number;
        ausfallReason?: string;
        isNachkodierung: boolean;
      }
    >({
      query: (params) => ({
        method: "POST",
        url: "Masterdata/ManualChangeRekStatusAndSubStatus",
        body: params,
      }),
    }),
    setThsDeclined: builder.mutation<
      ValidationErrors,
      { treuhandstelleId: number }
    >({
      query: (params) => ({
        method: "POST",
        url: "Treuhand/SetTreuhandstelleDeclined",
        params,
      }),
    }),
    deleteThs: builder.mutation<ValidationErrors, { treuhandstelleId: number }>(
      {
        query: (params) => ({
          method: "POST",
          url: "Treuhand/DeleteTreuhandstelle",
          params,
        }),
      }
    ),
    deleteTh: builder.mutation<ValidationErrors, { treuhaenderId: number }>({
      query: (params) => ({
        method: "POST",
        url: "Treuhand/DeleteTreuhaender",
        params,
      }),
    }),
    resetKundensatz: builder.mutation<
      ValidationErrors,
      { kundennummer: number }
    >({
      query: (params) => ({
        method: "POST",
        url: "Masterdata/ResetKundensatz",
        params,
      }),
    }),
    setOnboardingCode: builder.mutation<
      ValidationErrors,
      { kundennummer: number; datenlieferant: number }
    >({
      query: (params) => ({
        method: "POST",
        url: "Management/SetOnboardingCodeFromDatenlieferant",
        params,
      }),
    }),
  }),
});

export const {
  useLazyGetMasterdataQuery,
  useGetZammadBaseLinkQuery,
  useGetTreuhandstelleAutoCompleteQuery,
  useGetLandwirtKundensatzQuery,
  useGetTreuhandstelleQuery,
  useGetTreuhandstelleByIdQuery,
  useGetTreuhaenderQuery,
  useGetTreuhaenderByIdQuery,
  useGetErhebungsdatenPersonDataQuery,
  useValidateAndGetLandwirtDataMutation,
  useUpdateLandwirtMutation,
  useGetLandwirtMasterdataForTreuhaenderQuery,
  useUpdateTreuhaenderMutation,
  useGetTreuhaenderMasterdataForTreuhandstelleQuery,
  useUpdateTreuhandstelleMutation,
  useAddTreuhaenderMutation,
  useLazyFindTreuhandstelleQuery,
  useLazyFindTreuhaenderQuery,
  useSetDatenlieferantLandwirtMutation,
  useAddExistingTreuhaenderToKundensatzMutation,
  useAddExistingTreuhandstelleToKundensatzMutation,
  useAddTreuhandstelleMutation,
  useActivateThsQuestionnaireMutation,
  useGetLieferterminListQuery,
  useSaveLieferterminMutation,
  useSetTreuhaenderContactMailMutation,
  useDeleteTreuhaenderContactMailMutation,
  useGetPossibleRekSubStatusListMutation,
  useGetPossibleRekStatusListMutation,
  useSetRekStatusMutation,
  useSetThsDeclinedMutation,
  useDeleteThsMutation,
  useDeleteThMutation,
  useResetKundensatzMutation,
  useSetOnboardingCodeMutation,
} = masterdataApi;
